(() => {
  'use strict';
  const jsonCache = new Map();
  const cleanSku = value => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const esc = value => String(value ?? '').replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
  const json = url => {
    if (!jsonCache.has(url)) {
      jsonCache.set(url, fetch(url, { credentials: 'same-origin' }).then(response => {
        if (!response.ok) throw new Error('Request failed');
        return response.json();
      }).catch(error => { jsonCache.delete(url); throw error; }));
    }
    return jsonCache.get(url);
  };
  const productUrl = (root, handle) => {
    const url = new URL(root + 'products/' + encodeURIComponent(handle), window.location.origin);
    url.searchParams.set('view', 'mincost-parts-data');
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.toString();
  };
  const money = (amount, currency, locale) => new Intl.NumberFormat(locale || 'en-GB', { style: 'currency', currency: currency || 'GBP' }).format(amount / 100);
  const scrollBehavior = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  const imageUrl = (url, width = 1000) => {
    if (!url) return '';
    const result = new URL(url, window.location.origin);
    if (result.hostname === 'cdn.shopify.com') result.searchParams.set('width', String(width));
    return result.toString();
  };
  const updateCart = async root => {
    const response = await fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('[data-header-cart-count]').forEach(node => {
      node.dataset.headerCartCount = cart.item_count ? String(cart.item_count) : '';
      node.classList.toggle('visible', cart.item_count > 0);
    });
  };
  const addItem = async (root, variantId, quantity, properties) => {
    if (!Number.isInteger(quantity) || quantity < 1) throw new Error('quantity');
    const response = await fetch(root + 'cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity, properties }] })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'cart');
    updateCart(root).catch(() => {});
    return result;
  };
  class MincostPartsFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.config = JSON.parse(this.querySelector('[data-parts-config]').textContent);
      this.t = this.config.text;
      this.root = this.config.root;
      this.zoom = 1;
      this.selectionToken = 0;
      this.modelToken = 0;
      this.$ = selector => this.querySelector(selector);
      this.$('[data-model]').addEventListener('change', event => this.chooseModel(event.target.value));
      this.$('[data-assembly]').addEventListener('change', event => this.chooseDiagram(event.target.value));
      this.$('[data-search-form]').addEventListener('submit', event => { event.preventDefault(); this.search(); });
      this.$('[data-search]').addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.search(), 220);
      });
      this.$('[data-row-filter]').addEventListener('input', event => this.renderRows(event.target.value));
      this.$('[data-zoom-in]').addEventListener('click', () => this.setZoom(Math.min(4, this.zoom + .5)));
      this.$('[data-zoom-out]').addEventListener('click', () => this.setZoom(Math.max(1, this.zoom - .5)));
      this.$('[data-zoom-reset]').addEventListener('click', () => this.setZoom(1));
      this.$('[data-diagram-image]').addEventListener('load', () => this.sizeDiagram());
      this.$('[data-diagram-image]').addEventListener('error', () => {
        this.$('[data-image-loading]').hidden = true;
        this.$('[data-image-error]').hidden = false;
        this.$('[data-hotspots]').replaceChildren();
      });
      this.$('[data-part-form]').addEventListener('submit', event => this.addSelected(event));
      this.resizeObserver = new ResizeObserver(() => this.sizeDiagram());
      this.resizeObserver.observe(this.$('[data-diagram-viewport]'));
      this.load().catch(() => this.message(this.t.load_error, true));
    }
    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      clearTimeout(this.searchTimer);
    }
    message(text, error = false) {
      const target = this.$('[data-status]');
      target.textContent = text;
      target.classList.toggle('mpf-error', error);
    }
    async load() {
      this.index = await json(this.config.indexUrl);
      this.message('');
      const params = new URLSearchParams(window.location.search);
      const sku = cleanSku(params.get('part') || this.dataset.part);
      const pnc = cleanSku(params.get('model'));
      if (pnc && this.config.modelUrls[pnc]) {
        await this.chooseModel(pnc, params.get('diagram'), sku);
      } else if (sku && this.index.parts[sku]) {
        this.$('[data-search]').value = this.index.parts[sku].number;
        this.search();
        this.showModelMatches(sku);
      }
    }
    search() {
      clearTimeout(this.searchTimer);
      if (!this.index) return;
      const query = this.$('[data-search]').value.trim();
      const results = this.$('[data-search-results]');
      if (query.length < 2) { results.hidden = true; results.replaceChildren(); return; }
      const numeric = cleanSku(query);
      const words = query.toLowerCase().split(/\s+/);
      const matches = Object.values(this.index.parts).filter(part =>
        (/\d/.test(numeric) && part.sku.includes(numeric)) ||
        words.every(word => (part.title + ' ' + part.specification).toLowerCase().includes(word))
      ).sort((a, b) => Number(b.sku === numeric) - Number(a.sku === numeric)).slice(0, 25);
      results.hidden = false;
      results.innerHTML = '<p class="mpf-small">' + esc(matches.length ? this.t.search_results : this.t.no_results) + '</p>';
      for (const part of matches) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mpf-search-result';
        button.innerHTML = '<span><strong>' + esc(part.title) + '</strong><small>' + esc(part.models.length + ' ' + this.t.model_versions) + '</small></span><span aria-hidden="true">›</span>';
        button.addEventListener('click', () => this.showModelMatches(part.sku));
        results.append(button);
      }
    }
    showModelMatches(sku) {
      const part = this.index.parts[sku];
      if (!part) return;
      const results = this.$('[data-search-results]');
      results.hidden = false;
      results.replaceChildren();
      const heading = document.createElement('p');
      heading.className = 'mpf-small';
      heading.textContent = this.t.choose_version + ' ' + part.number;
      results.append(heading);
      for (const id of part.models) {
        const model = this.index.models.find(m => m.id === id);
        if (!model) continue;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mpf-search-result';
        button.innerHTML = '<span><strong>' + esc(model.name) + '</strong><small>' + esc(this.t.product_number + ': ' + id) + '</small></span><span aria-hidden="true">›</span>';
        button.addEventListener('click', () => {
          results.hidden = true;
          this.chooseModel(id, null, sku);
        });
        results.append(button);
      }
    }
    async chooseModel(id, requestedDiagram, sku) {
      const token = ++this.modelToken;
      this.selectionToken++;
      this.selectedRow = null;
      this.selectedVariant = null;
      this.$('[data-part-form]').hidden = true;
      this.$('[data-part-empty]').hidden = false;
      this.$('[data-part-details]').hidden = true;
      this.$('[data-workspace]').hidden = true;
      if (!id || !this.config.modelUrls[id]) { this.$('[data-workspace]').hidden = true; return; }
      this.message(this.t.loading);
      this.$('[data-model]').value = id;
      this.$('[data-assembly]').disabled = true;
      try {
        const model = await json(this.config.modelUrls[id]);
        if (token !== this.modelToken) return;
        this.model = model;
        this.$('[data-model-summary]').textContent = model.name + ' · ' + this.t.product_number + ' ' + model.id;
        const select = this.$('[data-assembly]');
        select.replaceChildren();
        for (const diagram of model.diagrams) {
          const option = document.createElement('option');
          option.value = diagram.id;
          option.textContent = diagram.name;
          select.append(option);
        }
        select.disabled = false;
        this.$('[data-workspace]').hidden = false;
        let diagram = model.diagrams.find(d => d.id === requestedDiagram);
        if (!diagram && sku) diagram = model.diagrams.find(d => d.rows.some(r => r.sku === sku));
        diagram ||= model.diagrams[0];
        this.chooseDiagram(diagram.id, sku);
        this.message('');
      } catch {
        if (token === this.modelToken) {
          this.message(this.t.load_error, true);
          this.$('[data-assembly]').disabled = false;
        }
      }
    }
    chooseDiagram(id, sku) {
      const diagram = this.model.diagrams.find(d => d.id === id);
      if (!diagram) return;
      this.selectionToken++;
      this.diagram = diagram;
      this.selectedRow = null;
      this.selectedVariant = null;
      this.zoom = 1;
      this.$('[data-assembly]').value = id;
      this.$('[data-diagram-title]').textContent = diagram.name;
      this.$('[data-row-filter]').value = '';
      this.$('[data-image-error]').hidden = true;
      this.$('[data-image-loading]').hidden = false;
      this.$('[data-hotspots]').hidden = true;
      this.$('[data-part-details]').hidden = true;
      this.$('[data-part-empty]').hidden = false;
      this.$('[data-part-empty-text]').textContent = this.t.select_help;
      this.$('[data-part-form]').hidden = true;
      this.$('[data-add-message]').textContent = '';
      const image = this.$('[data-diagram-image]');
      image.alt = this.model.name + ' — ' + diagram.name;
      image.src = imageUrl(diagram.image || diagram.sourceImage, 1800);
      this.$('[data-open-image]').href = diagram.image || diagram.sourceImage;
      this.renderRows();
      this.renderHotspots();
      this.writeUrl();
      if (sku) {
        const row = diagram.rows.find(r => r.sku === sku);
        if (row) this.showPart(row, false);
      }
    }
    writeUrl(sku) {
      const url = new URL(window.location.href);
      url.searchParams.set('model', this.model.id);
      url.searchParams.set('diagram', this.diagram.id);
      if (sku) url.searchParams.set('part', sku); else url.searchParams.delete('part');
      window.history.replaceState({}, '', url);
    }
    renderRows(filter = '') {
      const rows = this.$('[data-rows]');
      const query = filter.trim().toLowerCase();
      const ref = query.match(/^#(.+)$/)?.[1];
      rows.replaceChildren();
      const filtered = this.diagram.rows.filter(row => ref ? row.ref === ref : !query ||
        [row.ref, row.sku, row.name, row.note, this.index.parts[row.sku]?.specification || ''].join(' ').toLowerCase().includes(query) ||
        (/\d/.test(query) && row.sku.includes(cleanSku(query))));
      for (const row of filtered) {
        const tr = document.createElement('tr');
        tr.dataset.rowSku = row.sku;
        tr.dataset.rowRef = row.ref;
        tr.classList.toggle('mpf-selected-row', row === this.selectedRow);
        const part = this.index.parts[row.sku];
        tr.innerHTML = '<td><span class="mpf-ref">' + esc(row.ref) + '</span></td><td><button type="button" class="mpf-part-link">' + esc(part?.number || row.sku) + '</button></td><td><strong>' + esc(row.name) + '</strong>' + (row.note ? '<small>' + esc(row.note) + '</small>' : '') + '</td><td>' + esc(row.quantity) + '</td>';
        tr.querySelector('button').addEventListener('click', () => this.showPart(row));
        rows.append(tr);
      }
      if (!filtered.length) {
        const tr = document.createElement('tr');
        tr.innerHTML = '<td colspan="4">' + esc(this.t.no_results) + '</td>';
        rows.append(tr);
      }
      this.$('[data-row-count]').textContent = String(filtered.length) + ' ' + this.t.diagram_lines;
    }
    renderHotspots() {
      const target = this.$('[data-hotspots]');
      target.replaceChildren();
      const groups = new Map();
      for (const row of this.diagram.rows) {
        for (const point of row.coords) {
          const key = row.ref + ':' + point.join(',');
          if (!groups.has(key)) groups.set(key, { ref: row.ref, point, rows: [] });
          groups.get(key).rows.push(row);
        }
      }
      for (const group of groups.values()) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mpf-hotspot';
        button.textContent = group.ref;
        button.setAttribute('aria-label', this.t.reference + ' ' + group.ref + ': ' + [...new Set(group.rows.map(r => r.name))].join(', '));
        button.dataset.point = group.point.join(',');
        button.dataset.ref = group.ref;
        button.addEventListener('click', () => {
          if (group.rows.length === 1) this.showPart(group.rows[0]);
          else {
            this.selectedRow = null;
            this.selectedVariant = null;
            this.selectionToken++;
            this.$('[data-part-details]').hidden = true;
            this.$('[data-part-form]').hidden = true;
            this.$('[data-part-empty]').hidden = false;
            this.$('[data-part-empty-text]').textContent = this.t.multiple_parts;
            this.$('[data-row-filter]').value = '#' + group.ref;
            this.renderRows('#' + group.ref);
            this.highlight(group.ref);
            this.$('[data-parts-list]').scrollIntoView({ behavior: scrollBehavior(), block: 'nearest' });
          }
        });
        target.append(button);
      }
      this.sizeDiagram();
    }
    setZoom(value) { this.zoom = value; this.sizeDiagram(); }
    sizeDiagram() {
      if (!this.diagram) return;
      const image = this.$('[data-diagram-image]');
      if (!image.complete || !image.naturalWidth) return;
      this.$('[data-image-loading]').hidden = true;
      this.$('[data-hotspots]').hidden = false;
      const viewport = this.$('[data-diagram-viewport]');
      const width = this.diagram.width || image.naturalWidth;
      const height = this.diagram.height || image.naturalHeight;
      const scale = Math.min((viewport.clientWidth - 24) / width, (viewport.clientHeight - 24) / height) * this.zoom;
      const surface = this.$('[data-diagram-surface]');
      surface.style.width = Math.max(1, width * scale) + 'px';
      surface.style.height = Math.max(1, height * scale) + 'px';
      this.$('[data-zoom-value]').textContent = Math.round(this.zoom * 100) + '%';
      this.$('[data-zoom-out]').disabled = this.zoom <= 1;
      this.$('[data-zoom-in]').disabled = this.zoom >= 4;
      this.querySelectorAll('[data-point]').forEach(button => {
        const [x1, y1, x2, y2] = button.dataset.point.split(',').map(Number);
        button.style.left = ((x1 + x2) / 2 / width * 100) + '%';
        button.style.top = ((y1 + y2) / 2 / height * 100) + '%';
      });
    }
    highlight(ref, sku) {
      this.querySelectorAll('.mpf-hotspot').forEach(button => button.classList.toggle('mpf-hotspot-selected', button.dataset.ref === ref));
      this.querySelectorAll('[data-row-sku]').forEach(row => row.classList.toggle('mpf-selected-row', row.dataset.rowSku === sku && row.dataset.rowRef === ref));
    }
    async showPart(row, scroll = true) {
      const token = ++this.selectionToken;
      this.selectedRow = row;
      this.selectedVariant = null;
      const part = this.index.parts[row.sku];
      if (!part) return;
      this.highlight(row.ref, row.sku);
      this.writeUrl(row.sku);
      this.$('[data-part-empty]').hidden = true;
      this.$('[data-part-details]').hidden = false;
      this.$('[data-part-form]').hidden = true;
      this.$('[data-add-message]').textContent = '';
      this.$('[data-part-number]').textContent = part.number;
      this.$('[data-part-title]').textContent = part.specification || row.name;
      this.$('[data-part-context]').textContent = this.t.reference + ' ' + row.ref + ' · ' + this.diagram.name;
      this.$('[data-part-note]').textContent = row.note;
      this.$('[data-part-note]').hidden = !row.note;
      this.$('[data-part-price]').textContent = this.t.checking;
      this.$('[data-part-vat]').hidden = true;
      this.$('[data-part-availability]').textContent = '';
      this.$('[data-quantity]').value = '1';
      const image = this.$('[data-part-image]');
      image.hidden = !part.photo;
      if (part.photo) { image.src = imageUrl(part.photo, 700); image.alt = part.title; }
      const link = this.$('[data-product-link]');
      link.hidden = true;
      const enquiry = this.$('[data-enquiry]');
      enquiry.hidden = true;
      if (scroll && window.matchMedia('(max-width: 760px)').matches) this.$('[data-part-panel]').scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      if (part.state !== 'ready' || !part.handle || !part.variantId) {
        this.$('[data-part-price]').textContent = part.state === 'review' ? this.t.confirm_part : this.t.price_on_request;
        this.$('[data-part-availability]').textContent = part.state === 'archived' ? this.t.availability_check : this.t.enquiry_help;
        enquiry.hidden = false;
        return;
      }
      try {
        const product = await json(productUrl(this.root, part.handle));
        if (token !== this.selectionToken) return;
        const variant = product.variants?.find(v => String(v.id) === String(part.variantId) && cleanSku(v.sku) === row.sku);
        if (!variant || !variant.price || variant.price <= 0 || product.pricePending) throw new Error('unavailable');
        if (product.image) { image.src = imageUrl(product.image, 700); image.alt = product.title; image.hidden = false; }
        this.$('[data-part-price]').textContent = money(variant.price, this.config.currency, this.config.locale);
        this.$('[data-part-vat]').hidden = false;
        this.$('[data-part-availability]').textContent = variant.inStock ? this.t.in_stock : (variant.available && variant.inventoryPolicy === 'continue' ? this.t.available_to_order : this.t.availability_check);
        link.href = this.root + 'products/' + encodeURIComponent(part.handle) + '?variant=' + part.variantId;
        link.hidden = false;
        if (variant.available) {
          this.selectedVariant = variant;
          this.$('[data-part-form]').hidden = false;
          this.$('[data-add-button]').disabled = false;
          this.$('[data-add-button]').textContent = this.t.add_to_basket;
        } else enquiry.hidden = false;
      } catch {
        if (token !== this.selectionToken) return;
        this.$('[data-part-price]').textContent = this.t.price_on_request;
        this.$('[data-part-availability]').textContent = this.t.availability_check;
        enquiry.hidden = false;
      }
    }
    async addSelected(event) {
      event.preventDefault();
      if (!this.selectedVariant || !this.selectedRow) return;
      const button = this.$('[data-add-button]');
      const message = this.$('[data-add-message]');
      const quantityText = this.$('[data-quantity]').value;
      if (!/^[1-9]\d*$/.test(quantityText)) { message.textContent = this.t.quantity_error; return; }
      const variant = this.selectedVariant;
      const row = this.selectedRow;
      const token = this.selectionToken;
      const properties = { 'Mower': this.model.name, 'Product number': this.model.id, 'Diagram reference': this.diagram.name + ' / ' + row.ref };
      button.disabled = true;
      button.textContent = this.t.adding;
      message.textContent = '';
      try {
        await addItem(this.root, variant.id, Number(quantityText), properties);
        if (token !== this.selectionToken) return;
        message.innerHTML = esc(this.t.added) + ' <a href="' + esc(this.root + 'cart') + '">' + esc(this.t.view_basket) + '</a>';
      } catch (error) {
        if (token === this.selectionToken) message.textContent = error.message === 'quantity' ? this.t.quantity_error : this.t.cart_error;
      } finally {
        if (token === this.selectionToken) { button.disabled = false; button.textContent = this.t.add_to_basket; }
      }
    }
  }
  class MincostPartIllustration extends HTMLElement {
    async connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      try {
        const config = JSON.parse(this.querySelector('[data-parts-config]').textContent);
        const index = await json(config.indexUrl);
        const part = index.parts[cleanSku(this.dataset.sku)];
        if (!part) return;
        const model = await json(config.modelUrls[part.models[0]]);
        const diagram = model.diagrams.find(d => d.rows.some(r => r.sku === part.sku));
        const row = diagram?.rows.find(r => r.sku === part.sku);
        if (!diagram || !row) return;
        const image = this.querySelector('img');
        const stage = this.querySelector('[data-illustration-stage]');
        image.alt = model.name + ' — ' + diagram.name;
        image.onload = () => {
          const w = diagram.width || image.naturalWidth;
          const h = diagram.height || image.naturalHeight;
          for (const [x1,y1,x2,y2] of row.coords) {
            const marker = document.createElement('span');
            marker.className = 'mpf-illustration-marker';
            marker.textContent = row.ref;
            marker.style.left = ((x1+x2)/2/w*100) + '%';
            marker.style.top = ((y1+y2)/2/h*100) + '%';
            stage.append(marker);
          }
        };
        image.src = imageUrl(diagram.image || diagram.sourceImage, 1200);
        this.querySelector('[data-illustration-caption]').textContent = config.text.reference + ' ' + row.ref + ' · ' + diagram.name;
        this.querySelector('a').href = config.finderUrl + '?model=' + model.id + '&diagram=' + encodeURIComponent(diagram.id) + '&part=' + part.sku;
        this.hidden = false;
      } catch { this.querySelector('[data-illustration-caption]').textContent = ''; }
    }
  }
  class MincostPartProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      const data = JSON.parse(this.querySelector('[data-product-config]').textContent);
      const form = this.querySelector('form');
      const select = this.querySelector('[name="id"]');
      const button = this.querySelector('[data-product-add]');
      const quantity = this.querySelector('[name="quantity"]');
      const status = this.querySelector('[data-product-status]');
      const message = this.querySelector('[data-product-message]');
      const refresh = () => {
        const variant = data.variants.find(v => String(v.id) === String(select.value));
        if (!variant) return;
        this.querySelector('[data-product-price]').textContent = data.pricePending ? data.text.price_on_request : money(variant.price, data.currency, data.locale);
        status.textContent = variant.inStock ? data.text.in_stock : (variant.available && variant.inventoryPolicy === 'continue' ? data.text.available_to_order : data.text.availability_check);
        button.disabled = data.pricePending || !variant.available || variant.price <= 0;
        this.querySelector('[data-product-sku]').textContent = variant.sku;
      };
      select.addEventListener('change', refresh);
      form.addEventListener('submit', async event => {
        event.preventDefault();
        const variant = data.variants.find(v => String(v.id) === String(select.value));
        if (!variant || !variant.available || variant.price <= 0 || data.pricePending) return;
        if (!/^[1-9]\d*$/.test(quantity.value)) { message.textContent = data.text.quantity_error; return; }
        button.disabled = true;
        button.textContent = data.text.adding;
        try {
          await addItem(data.root, select.value, Number(quantity.value), {});
          message.innerHTML = esc(data.text.added) + ' <a href="' + esc(data.root + 'cart') + '">' + esc(data.text.view_basket) + '</a>';
        } catch { message.textContent = data.text.cart_error; }
        finally { button.textContent = data.text.add_to_basket; refresh(); }
      });
    }
  }
  if (!customElements.get('mincost-parts-finder')) customElements.define('mincost-parts-finder', MincostPartsFinder);
  if (!customElements.get('mincost-part-illustration')) customElements.define('mincost-part-illustration', MincostPartIllustration);
  if (!customElements.get('mincost-part-product')) customElements.define('mincost-part-product', MincostPartProduct);
})();
